import { useLazyQuery } from '@apollo/client';
import {
  AccessControl,
  ALL_MODULES,
  EventEmitter,
  getConversationOrder,
  getCurrentUserHook,
  getTranslatedText,
  TICKET_SOURCE,
  useUserMSPCheckHook,
  COMMON_CONSTANTS
} from '@nile/components';
import GlobalChatBoxV2 from '@nile/components/src/Components/GlobalChatBoxV2';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import TimerActionsFromReply from '@nile/components/src/Containers/NewWorklog/TimerActionsFromReply';
import {
  AccountClient,
  GET_CLIENT_USERS,
  GET_DEFAULT_REPLY_TEMPLATE,
  GET_TICKET_AUDIT_LIST,
  TicketClient,
  GET_IM_CONFIG_DETAILS
} from '@nile/graphql';
import { GET_TICKET_REPLY_RECIPEINTS } from '@nile/graphql/src/Query/ticket';
import envConfig from '@nile/home/src/Config/env';
import SendTicketConversationAction from '@nile/mangifera/src/Containers/ChatContainers/SendTicketConversationAction';
import SendTicketNoteAction from '@nile/mangifera/src/Containers/ChatContainers/SendTicketNoteAction';
import TicketNoteHeaderAction from '@nile/mangifera/src/Containers/ChatContainers/TicketNoteHeaderAction';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { useTicketConversationActionStore } from '@nile/mangifera/src/StoreManager/store/TicketDetails/TicketConversationActionStore';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import TicketConversationHeaderAction from './TicketConversationHeaderAction';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TimerActions = ({ editorRef, data }) => {
  const workItem = data?.data?.workItem;
  return (
    <AccessControl
      roleToCheck={{
        roles: ['tickets.edit', 'serviceCatalog.view']
      }}
      render={
        <div className="fx actr fg1">
          <TimerActionsFromReply
            froalaEditor
            editorRef={editorRef}
            workItem={workItem}
            bannerKey="globalReplyBanner"
          />
        </div>
      }
    />
  );
};

interface Recipient {
  userId: string;
  name: string;
  email: string;
}

interface Mailbox {
  boxId: string;
  outgoingAddress: string;
  outgoingDomainVerified: boolean;
}

interface ConversationRecipients {
  to?: Recipient[] | null;
  cc?: Recipient[] | null;
  bccUsers?: Recipient[] | null;
  hasAdditionalRecipients?: boolean;
  conversationThreads?: any[] | null;
  selectedMailbox?: Mailbox | null;
}

const TicketConversationActionHook = ({ ticket }) => {
  const ticketId = ticket?.ticketId;
  const tabContext = useContext(TabContext);
  const intl = useIntl();
  const isMSP = useUserMSPCheckHook();
  const currentUser = getCurrentUserHook();
  const conversationOrder = getConversationOrder() || 'ASC';
  const flags = useFlags();
  const imAppSupport = flags?.imAppSupport;

  // NOTE_EDITOR_CONFIG
  const NOTE_EDITOR_STORE_CONFIG = {
    id: ticketId,
    name: 'ticket_note',
    cacheConfig: {
      scope: 'global'
    }
  };

  // CONVERSATION_EDITOR_CONFIG
  const CONVERSATION_EDITOR_STORE_CONFIG = {
    id: ticketId,
    name: 'ticket_conversation',
    cacheConfig: {
      scope: 'global'
    }
  };

  // NOTE_EDITOR STORE
  const {
    template: noteTemplate,
    resetTemplate: resetNoteTemplate
  } = useSuperEditor(NOTE_EDITOR_STORE_CONFIG);

  // CONVERSATION_EDITOR_STORE
  const {
    template: conversationTemplate,
    updateTemplate: updateConversationTemplate,
    resetTemplate: resetConversationTemplate
  } = useSuperEditor(CONVERSATION_EDITOR_STORE_CONFIG);

  // TICKET_CONVERSATION_ACTIONS_STORE
  const ticketConversationStore = useTicketConversationActionStore({
    ticketId: ticketId
  });

  // NOTES DATA FROM STORE

  const resetNotesData = ticketConversationStore(
    (state) => state.resetNotesData
  );
  // END NOTES DATA FROM STORE

  // CONVERSATION DATA FROM STORE
  const conversationTo = ticketConversationStore(
    (state) => state.conversation?.to
  );
  const setConversationTo = ticketConversationStore(
    (state) => state.setConversationTo
  );

  const conversationCC = ticketConversationStore(
    (state) => state.conversation?.cc
  );
  const setConversationCC = ticketConversationStore(
    (state) => state.setConversationCC
  );

  const conversationBCC = ticketConversationStore(
    (state) => state.conversation?.bcc
  );

  const setConversationBCC = ticketConversationStore(
    (state) => state.setConversationBCC
  );

  const setSelectedConversationMailbox = ticketConversationStore(
    (state) => state.setSelectedConversationMailbox
  );

  const resetConversationStore = ticketConversationStore(
    (state) => state.resetConversationData
  );

  const conversationScope = ticketConversationStore(
    (state) => state.conversation?.conversationScope
  );

  const setConversationScope = ticketConversationStore(
    (state) => state.setConversationScope
  );

  const setIsTriageConversation = ticketConversationStore(
    (state) => state.setIsTriageConversation
  );
  // END CONVERSATION DATA FROM STORE

  // RESET FUNCTIONS
  const resetTicketNotesData = () => {
    resetNotesData();
    resetNoteTemplate();
  };

  const resetConversationData = () => {
    resetConversationTemplate();
    resetConversationStore();
  };

  // END RESET FUNCTIONS
  const openTicket = () => {
    tabContext.openTab({
      module: 'TICKET',
      ticketId: ticket.ticketId,
      data: {
        subject: ticket.subject,
        displayId: ticket.displayId,
        ticketId: ticket.ticketId
      }
    });
  };

  const formatTicketForWorkItem = (ticket) => {
    if (ticket) {
      return {
        id: ticket?.ticketId,
        workId: ticket?.ticketId,
        module: 'TICKET',
        subject: ticket?.subject,
        client: {
          name: ticket?.client?.name,
          accountId: ticket?.client?.accountId
        },
        requester: ticket?.requester,
        ticketId: ticket?.ticketId
      };
    }
  };
  // END FORMAT HELPERS

  const getParams = (mode) => {
    switch (mode) {
      case 'conversation':
        return {
          data: {
            heading: getTranslatedText(intl, 'common.send.reply'),
            icon: 'Reply',
            iconBg: 'success'
          },
          actions: {
            secondaryAction: {
              label: getTranslatedText(intl, 'common.cancel')
            },
            onClose: () => {
              resetConversationData();
            }
          },
          config: {
            hasWorklog: true,
            editor: {
              placeholder: getTranslatedText(
                intl,
                'common.placeholder.writeHere'
              ),
              details: {
                workItem: {
                  module: 'TICKET',
                  workId: ticketId
                }
              },
              focusEditor: true,
              module: ALL_MODULES.ticket,
              allowMentions: true,
              attachmentUploadUrl: `${envConfig.fileRoot}/upload`,
              uploadParams: {
                module: ALL_MODULES.ticketConversationAttachment,
                details: JSON.stringify({
                  ticketId: ticket.ticketId
                })
              },
              storeConfig: CONVERSATION_EDITOR_STORE_CONFIG
            },
            agentCollision: {
              entityId: ticketId,
              sendEventConfig: {
                entityId: ticketId,
                entity: 'Ticket'
              }
            }
          },
          components: {
            headerAction: {
              component: isMSP && TicketConversationHeaderAction
            },
            primaryAction: {
              component: SendTicketConversationAction
            },
            toolbarAction: { component: TimerActions }
          }
        };
      case 'note':
        return {
          data: {
            heading: getTranslatedText(intl, 'ticket.details.chat.addNote'),
            icon: 'Notes',
            iconBg: 'attention'
          },
          actions: {
            secondaryAction: {
              label: getTranslatedText(intl, 'common.cancel')
            },
            onClose: () => {
              resetTicketNotesData();
            }
          },
          config: {
            hasWorklog: true,
            editor: {
              placeholder: getTranslatedText(
                intl,
                'ticket.details.chat.pl.desc'
              ),
              details: {
                workItem: {
                  module: 'TICKET',
                  workId: ticketId
                }
              },
              focusEditor: true,
              module: ALL_MODULES.ticket,
              allowMentions: true,
              attachmentUploadUrl: `${envConfig.fileRoot}/upload`,
              uploadParams: {
                module: ALL_MODULES.ticketNoteAttachment,
                details: JSON.stringify({
                  ticketId: ticket.ticketId
                })
              },
              storeConfig: NOTE_EDITOR_STORE_CONFIG
            }
          },
          components: {
            headerAction: { component: TicketNoteHeaderAction },
            toolbarAction: { component: TimerActions },
            primaryAction: {
              component: SendTicketNoteAction
            }
          }
        };
      default:
        return null;
    }
  };

  const [getIMConfig, { loading: imConfigLoading }] = useLazyQuery(
    GET_IM_CONFIG_DETAILS,
    {
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      client: AccountClient
    }
  );

  const [
    getDefaultReplyTemplate,
    { loading: defaultTemplateLoading }
  ] = useLazyQuery(GET_DEFAULT_REPLY_TEMPLATE, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    client: TicketClient,
    variables: {
      input: {
        ticket: {
          ticketId: ticket?.ticketId
        }
      }
    }
  });

  const [
    getDefaultReplyRecipients,
    { loading: defaultRecipientsLoading }
  ] = useLazyQuery(GET_TICKET_REPLY_RECIPEINTS, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    client: TicketClient,
    variables: {
      ticketInput: {
        ticketId: ticket?.ticketId
      }
    }
  });

  const sanitizeReplyTemplate = (template) => {
    const sanitizedTemplate = template
      ? template.replace(/[\u200B-\u200D\uFEFF]/g, '')
      : null; // Regex to remove &#xfeff in froala editor

    return sanitizedTemplate;
  };

  // GET DEFAULT CONVERSATION DATA (template, recipients)
  const getDefeaultConversationTemplateData = async (prefillTemplate) => {
    const prefillTemplateContent = prefillTemplate?.content;
    const prefillTemplateAttachments = prefillTemplate?.attachments;

    try {
      const res = await getDefaultReplyTemplate();
      const defaultReplycontentFromTemplate =
        res?.data?.getConversationContentFromReplyTemplate?.content;
      const emailSignature = currentUser?.emailSignature
        ? `<p><br></p><p><br></p>${currentUser?.emailSignature}`
        : null;
      let defaulReplyTemplate =
        defaultReplycontentFromTemplate || emailSignature;
      if (prefillTemplateContent) {
        defaulReplyTemplate = `${prefillTemplateContent}<p><br></p><p><br></p>${defaulReplyTemplate}`;
      }
      let attachments = prefillTemplateAttachments?.length && [
        ...prefillTemplateAttachments
      ];
      const attachmentsFromReplyTemplate =
        res?.data?.getConversationContentFromReplyTemplate?.attachments;
      if (
        attachmentsFromReplyTemplate &&
        attachmentsFromReplyTemplate?.length
      ) {
        attachments = [...attachments, ...attachmentsFromReplyTemplate];
      }

      if (defaulReplyTemplate || attachmentsFromReplyTemplate) {
        defaulReplyTemplate = sanitizeReplyTemplate(defaulReplyTemplate);
        updateConversationTemplate({
          content: defaulReplyTemplate,
          attachments: attachments || []
        });
        return {
          content: defaulReplyTemplate,
          attachments: attachments || []
        };
      }
    } catch (error) {
      console.log(error);
    }

    if (prefillTemplateContent || prefillTemplateAttachments) {
      updateConversationTemplate({
        content: prefillTemplateContent,
        attachments: prefillTemplateAttachments || []
      });
      return {
        content: prefillTemplateContent,
        attachments: prefillTemplateAttachments || []
      };
    }
    return null;
  };

  const getDefaultRecipientsData = async () => {
    try {
      const res = await getDefaultReplyRecipients();
      const defaultRecipients = res?.data?.getTicketConversationReplyRecipients;
      return defaultRecipients;
    } catch (error) {
      console.log(error);
    }
    return null;
  };
  // END GET DEFAULT CONVERSATION DATA

  const usersConfig = {
    property: 'userId',
    query: GET_CLIENT_USERS,
    params: {
      client: AccountClient,
      variables: { input: { clientId: ticket?.client?.accountId } }
    },
    methodName: 'getClientUserAssociationsList'
  };

  const getFormattedUsers = (users: any[]) => {
    return (
      users?.map((user) => ({
        id: user.userId,
        name: user?.name,
        email: user.email,
        label: user?.name || user?.email,
        value: user?.name || user?.email
      })) || []
    );
  };

  const getRecipientsConfig = (recipients: ConversationRecipients) => {
    if (recipients) {
      if (ticket?.source === TICKET_SOURCE.Communication) {
        return null;
      }
      const storeData = ticketConversationStore.getState();
      const conversationData = storeData?.conversation;

      if (recipients?.selectedMailbox) {
        if (!conversationData?.selectedMailbox) {
          setSelectedConversationMailbox({
            ...recipients?.selectedMailbox,
            label: recipients?.selectedMailbox?.outgoingAddress,
            value: recipients?.selectedMailbox?.outgoingAddress
          });
        }
      }

      let ccUsers = conversationData?.cc || null;
      let bccUsers = conversationData?.bcc || null;

      if (!ccUsers && recipients?.cc) {
        const formattedCCUsers = getFormattedUsers(recipients?.cc);
        setConversationCC(formattedCCUsers);
        ccUsers = formattedCCUsers;
      }

      if (!bccUsers && recipients?.bcc) {
        const formattedBCCUsers = getFormattedUsers(recipients?.bcc);
        setConversationBCC(formattedBCCUsers);
        bccUsers = formattedBCCUsers;
      }
      return {
        to: { value: recipients?.to?.[0] || null },
        cc: {
          value: ccUsers,
          onChange: setConversationCC,
          config: {
            ...usersConfig,
            isMulti: true,
            creatable: true,
            isClearable: true
          }
        },
        bcc: {
          value: bccUsers,
          onChange: setConversationBCC,
          config: { ...usersConfig, isMulti: true, isClearable: true }
        },
        conversationThreads: recipients?.conversationThreads || null,
        hasAdditionalRecipients: recipients?.hasAdditionalRecipients || false
      };
    }
    return null;
  };

  const getDefaultConversationType = async () => {
    // Get default conversation type based on the latest thread conversation scope
    const response = await TicketClient.query({
      query: GET_TICKET_AUDIT_LIST,
      variables: {
        input: {
          ticket: { ticketId: ticketId },
          listInfo: {
            page: 1,
            pageSize: 1,
            sort: [{ attribute: 'time', order: 'DESC' }],
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'type',
                  operator: 'is',
                  value: 'THREAD_CONVERSATION'
                }
              ]
            }
          }
        }
      }
    });

    const latestThreadedCoversation =
      response?.data?.getTicketAuditList?.ticketAudit?.[0]?.threadConversation;

    return latestThreadedCoversation;
  };

  const openChatPopup = async ({
    mode,
    prefillTemplate,
    triageConversation = false
  }) => {
    const params = getParams(mode);
    const additionalData = params?.data || {};
    const additionalActions = params?.actions || {};
    const additionalConfig = params?.config || {};
    const additionalComponents = params?.components || {};
    if (
      mode === 'conversation' &&
      ticket?.source === TICKET_SOURCE.Communication
    ) {
      let imConfig = null;
      if (imAppSupport) {
        const response = await getIMConfig();
        imConfig = response?.data?.getIMConfigDetails;
        additionalData.imConfig = imConfig;
      }
    }

    if (mode === 'conversation' && isMSP) {
      if (
        !conversationTemplate &&
        ticket?.source !== TICKET_SOURCE.Communication
      ) {
        const defaultConversationData = await getDefeaultConversationTemplateData(
          prefillTemplate
        );
        additionalConfig.defaultTemplate = defaultConversationData;
        // Will update the editor with default reply template and return the default template to set in chat params
      }
      const defaultRecipientsData = await getDefaultRecipientsData();
      if (ticket?.source === TICKET_SOURCE.Communication) {
        // Set the default conversation type based on the latest thread conversation scope
        const latestThreadedConversation = await getDefaultConversationType();
        const conversationScopeData =
          latestThreadedConversation?.conversationScope;

        if (!conversationScope) {
          setConversationScope(conversationScopeData);
        }

        const toUsers = [];
        defaultRecipientsData?.conversationThreads.forEach((thread) => {
          const recipients = thread?.recipients?.receiver;
          if (recipients?.length > 0) {
            recipients.forEach((el) => {
              if (el?.recipientType === 'RECEIVER' && el?.user) {
                toUsers.push({
                  ...el,
                  label: el?.user?.name,
                  value: el?.user?.email,
                  isFixed: true
                });
              }
            });
          }
        });
        additionalData.recipients = defaultRecipientsData;

        if (toUsers?.length > 0 && !conversationTo) {
          setConversationTo(toUsers);
        }
      } else {
        const recipientsConfig = getRecipientsConfig(defaultRecipientsData);
        additionalConfig.recipients = recipientsConfig;
      }

      if (triageConversation) {
        setIsTriageConversation(true);
      }
    }

    const CHAT_PARAMS = {
      key: `${mode}_${ticketId}`,
      id: ticketId,
      subject: ticket?.subject,
      module: ALL_MODULES.TICKET,
      component: GlobalChatBoxV2,
      type: 'GLOBAL_CHAT',
      components: additionalComponents,
      data: {
        ticket: ticket,
        mode: mode,
        workItem: formatTicketForWorkItem(ticket),
        ...additionalData
      },
      actions: {
        onSubjectClick: openTicket,
        ...additionalActions
      },
      config: {
        ...additionalConfig
      }
    };

    EventEmitter.dispatch('createGlobalConversation', CHAT_PARAMS);
  };

  return {
    openChatPopup,
    conversationLoading: defaultTemplateLoading || defaultRecipientsLoading
  };
};

export default TicketConversationActionHook;
